import * as React from 'react';
import { Fragment } from 'react';
import { compose, branch, renderNothing, lifecycle, withHandlers, withState } from 'recompose';
import { connect } from 'react-redux';
import { withRouter, Switch, Redirect } from 'react-router-dom';
import { State } from 'reducers';
import { skin, OemProps } from 'ui/styles/skin';
import { getTopBarVisible, isLoggedIn, getPermissions } from 'selectors/shellSelectors';
import { isLoading } from 'selectors/appSelectors';
import { TopBar } from './header/TopBar';
import { ReportsPage } from 'ui/pages/reports/ReportsPage';
import { DesignerPage } from 'ui/pages/designer/DesignerPage';
import { MonitoringPage } from 'ui/pages/monitoring/MonitoringPage';
import { waitUntilLoaded } from 'ui/shared/waitUntilLoaded';
import { LoginScreen } from 'ui/login/LoginScreen';
import { Fade, FadeTranslate } from 'ui/transitions';
import { toUrl } from './header/Navigation';
import { Route } from 'react-router';
import { ChangeImagePage } from 'ui/pages/changeImage/ChangeImagePage';
import { InstantMessageContainer } from 'ui/main/InstantMessageContainer';
import { Point } from 'logic/reports';

import './Shell.css';

type Visible = { visible: boolean };
type LoggedIn = { loggedIn: boolean };

const urls = {
    monitoring: toUrl('monitoring'),
    reports: toUrl('reports'),
    reportDesigner: toUrl('reports', ''),
};

const IfLoggedIn = (component: React.ComponentType<Visible>) =>
    branch<Visible & LoggedIn>(({ loggedIn }: LoggedIn) => !loggedIn, renderNothing)(component)
;

const monitoringFadeFrom = { x: '-100%', opacity: 1 };
const monitoringFadeTo = { x: '0%', opacity: 1 };

const TransitioningMonitoringPage = IfLoggedIn(({ visible }) =>
    <FadeTranslate
        from={ monitoringFadeFrom }
        to={ monitoringFadeTo }
        duration={ 400 }
        visible={ visible }
        className="abs"
        unmountOnExit={ true }
    >
        <MonitoringPage/>
    </FadeTranslate>
);


const reportsFadeFrom = { x: '100%', opacity: 1 };
const reportsFadeTo = { x: '0%', opacity: 1 };

const TransitioningReportsPage = IfLoggedIn(({ visible }) =>
    <FadeTranslate
        from={ reportsFadeFrom }
        to={ reportsFadeTo }
        duration={ 400 }
        visible={ visible }
        className="abs"
        unmountOnExit={ true }
    >
        <ReportsPage/>
    </FadeTranslate>
);


const TransitioningReportDesignerPage = IfLoggedIn(({ visible }) =>
    <Fade
        duration={ 400 }
        visible={ visible }
        className="abs"
        unmountOnExit={ true }
    >
        <DesignerPage visible={ visible }/>
    </Fade>
);


type Props = OwnProps & RouterProps & StateProps & RecomposeProps & OemProps;

const nullComponent = () => null;

const RawShell = ({ showTopBar, loggedIn, canExecuteReports, allowJoinSession, styles, location }: Props) =>
    <Fragment>
        <Fade visible={ loggedIn } duration={ 200 } unmountOnExit={ true }>
            <div id="shell">
                <Switch>
                    <Route exact={ true } path="/Client/image" component={ ChangeImagePage }/>

                    <Route path="/">
                        <Fragment>
                            { showTopBar &&
                                <TopBar allowJoinSession={ allowJoinSession }/>
                            }
                            <div className={ `body ${showTopBar ? '' : 'embedded'}` } style={ styles.Workspace }>
                                <TransitioningMonitoringPage
                                    loggedIn={ loggedIn }
                                    visible={ location.pathname === urls.monitoring }
                                />
                                <TransitioningReportsPage
                                    loggedIn={ loggedIn }
                                    visible={ canExecuteReports && location.pathname.indexOf(urls.reports) === 0 }
                                />
                                <TransitioningReportDesignerPage
                                    loggedIn={ loggedIn }
                                    visible={ canExecuteReports && location.pathname.indexOf(urls.reportDesigner) === 0 }
                                />
                                <Switch>
                                    <Route path={ urls.monitoring } component={ nullComponent }/>
                                    <Route path={ urls.reports } component={ nullComponent }/>

                                    <Redirect to={ urls.monitoring }/>
                                </Switch>

                                <InstantMessageContainer/>
                            </div>
                        </Fragment>
                    </Route>
                </Switch>
            </div>
        </Fade>
        <Fade visible={ !loggedIn } duration={ 200 }>
            <LoginScreen loggedIn={ loggedIn }/>
        </Fade>
    </Fragment>
;

export type OwnProps = {
};
export type RouterProps = {
    location: {
        pathname: string;
    };
};
type StateProps = {
    showTopBar: boolean;
    loading: boolean;
    loggedIn: boolean;
    canExecuteReports: boolean;
};
type RecomposeProps = {
    allowJoinSession: boolean;
    handleInputEvent: (e: MouseEvent) => void;
    handleInactivityTimeout: () => void;
};
type _RecomposeProps = RecomposeProps & {
    _setAllowJoinSession: (b: boolean) => void;
};

const mapStateToProps = (state: State): StateProps => ({
    showTopBar: getTopBarVisible(state),
    loading: isLoading(state),
    loggedIn: isLoggedIn(state),
    canExecuteReports: getPermissions(state).executeReport,
});

let inactivityTimerId: number;
let lastPoint: Point = { x: 0, y: 0 };

const INACTIVE_TIMEOUT = 10 * 60 * 1000; // 10 minutes

const watchForInactiveSession = (handleTimeout: Function) => {
    clearTimeout(inactivityTimerId);
    inactivityTimerId = setTimeout(handleTimeout, INACTIVE_TIMEOUT);
};


export const Shell = compose<Props, OwnProps>(
    skin,
    withRouter,
    connect<StateProps, {}, OwnProps>(mapStateToProps),
    waitUntilLoaded({ size: 'xlarge' }),
    withState('allowJoinSession', '_setAllowJoinSession', true),
    withHandlers<Props & _RecomposeProps, Partial<RecomposeProps>>({
        handleInactivityTimeout: ({ _setAllowJoinSession }) => () => {
            // AIR-36313, AIR-36727
            // The session has sat idle long enough that we don't trust a user that starts
            // interacting with the app (and attempts to click through into ServerConfig)
            _setAllowJoinSession(false);
        },
    }),
    withHandlers<Props, Partial<RecomposeProps>>({
        handleInputEvent: ({ handleInactivityTimeout }) => e => {
            if (e.type === 'mousemove') {
                // Having Task Manager open causes mousemove to fire every second!
                if (lastPoint && lastPoint.x === e.screenX && lastPoint.y === e.screenY) {
                    return;
                }
                lastPoint = {
                    x: e.screenX,
                    y: e.screenY,
                };
            }
            watchForInactiveSession(handleInactivityTimeout);
        },
    }),
    lifecycle<Props, {}>({
        componentDidMount() {
            const { handleInputEvent, handleInactivityTimeout } = this.props;
            window.addEventListener('click', handleInputEvent);
            window.addEventListener('mousemove', handleInputEvent);
            window.addEventListener('keypress', handleInputEvent);
            watchForInactiveSession(handleInactivityTimeout);
        },
        componentWillUnmount() {
            const { handleInputEvent } = this.props;
            window.removeEventListener('click', handleInputEvent);
            window.removeEventListener('mousemove', handleInputEvent);
            window.removeEventListener('keypress', handleInputEvent);
        },
    }),
)(RawShell);



// WEBPACK FOOTER //
// ./src/ui/main/Shell.tsx